/**
 * localStorage CRUD for the new StructuredWorldview shape.
 *
 * Parallel to the legacy SavedWorldview (markdown blob) library in
 * storage.ts. The two coexist during migration: the Analyzer flow writes
 * here AND mirrors a SavedWorldview entry so the existing library /
 * chat / debate surfaces keep working unchanged.
 *
 * Once the migration is complete (all surfaces consume StructuredWorldview
 * directly), the markdown mirror goes away and storage.ts can be deleted.
 */

import type {
  Case,
  ChatSettings,
  ForbiddenMove,
  StructuredWorldview,
  VoiceQuote,
  WorldviewContract,
} from '@arena/shared';

const STORAGE_KEY = 'arena.structuredWorldviews.v1';

function readAll(): StructuredWorldview[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Best-effort filter to defensible shape.
    return parsed.filter(
      (x): x is StructuredWorldview =>
        x &&
        typeof x === 'object' &&
        typeof (x as { id?: unknown }).id === 'string' &&
        Array.isArray((x as { cases?: unknown }).cases) &&
        typeof (x as { contract?: unknown }).contract === 'object',
    );
  } catch {
    return [];
  }
}

function writeAll(items: StructuredWorldview[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // quota / unavailable — swallow
  }
}

export function listStructuredWorldviews(): StructuredWorldview[] {
  return readAll().sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function getStructuredWorldview(id: string): StructuredWorldview | undefined {
  return readAll().find((w) => w.id === id);
}

export interface CreateStructuredWorldviewInput {
  name?: string;
  contract: WorldviewContract;
  cases: Case[];
  voiceCorpus: VoiceQuote[];
  settings?: ChatSettings;
}

export function saveStructuredWorldview(
  input: CreateStructuredWorldviewInput,
): StructuredWorldview {
  const now = new Date().toISOString();
  const wv: StructuredWorldview = {
    id: `swv_${Date.now()}`,
    name: input.name,
    contract: input.contract,
    cases: input.cases,
    voiceCorpus: input.voiceCorpus,
    settings: input.settings,
    createdAt: now,
    updatedAt: now,
  };
  const all = readAll();
  all.push(wv);
  writeAll(all);
  return wv;
}

export function deleteStructuredWorldview(id: string): void {
  writeAll(readAll().filter((w) => w.id !== id));
}

// ─── Defaults ──────────────────────────────────────────────────────────────

/**
 * The default contract scaffolding for an Analyzer-built worldview.
 *
 * Forbidden moves are seeded with universally-applicable ones; the user can
 * add more in future phases (when contract-editing UX lands). The binding
 * line is fixed — it's the empirically-grounded fidelity-over-agreeableness
 * directive from the research report.
 */
export function defaultContract(name: string): WorldviewContract {
  const forbiddenMoves: ForbiddenMove[] = [
    {
      move: 'collapse into balanced, agreeable both-sides hedging when pressed',
      why: 'the brain exists to represent how this person actually thinks, not to manage social comfort',
    },
    {
      move: 'invent specific biographical details that were not provided',
      why: 'fabricated personal facts will be exploited by a sharp debate opponent',
    },
    {
      move: 'refer to myself as an AI, a model, or roleplaying',
      why: 'breaks the frame of the brain being the person',
    },
  ];

  const outputRules: string[] = [
    'Write in flowing prose as if speaking aloud.',
    'Do not bullet-point or use headings unless explicitly asked.',
    'Never use hyphens or dashes as punctuation. Use commas, periods, or rewrite.',
  ];

  return {
    identity: {
      name,
      oneLine: `${name}'s brain — a worldview built from concrete cases of how I actually reason.`,
    },
    anchorFacts: [],
    forbiddenMoves,
    outputRules,
    bindingLine: `If forced to choose between sounding agreeable and sounding like ${name}, choose ${name}.`,
  };
}
